import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export interface GymSettings {
  id: string;
  organization_id: string;
  organization_name: string | null;
  organization_slug: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  terms_file_url: string | null;
  created_at: string;
  updated_at: string;
}

export const useGymSettings = () => {
  const [settings, setSettings] = useState<GymSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { currentOrganization } = useAuth();

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .rpc('get_organization_info', {
          p_organization_id: currentOrganization?.id
        });

      if (error) throw error;

      setSettings(data?.[0] || null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching gym settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (updates: {
    organization_name?: string;
    organization_slug?: string;
    logo_light_url?: string;
    logo_dark_url?: string;
    terms_file_url?: string;
    clear_light_logo?: boolean;
    clear_dark_logo?: boolean;
    clear_terms?: boolean;
  }) => {
    try {
      console.log('📝 Starting settings update:', updates);
      
      const { error } = await supabase
        .rpc('update_organization_info', {
          p_organization_id: currentOrganization?.id,
          p_organization_name: updates.organization_name,
          p_organization_slug: updates.organization_slug,
          p_logo_light_url: updates.logo_light_url,
          p_logo_dark_url: updates.logo_dark_url,
          p_terms_file_url: updates.terms_file_url,
          p_clear_light_logo: updates.clear_light_logo || false,
          p_clear_dark_logo: updates.clear_dark_logo || false,
          p_clear_terms: updates.clear_terms || false,
        });

      console.log('💾 Database update result:', { error });

      if (error) throw error;

      // Refresh settings
      console.log('🔄 Refreshing settings...');
      await fetchSettings();
      
      toast({
        title: "Success",
        description: "Settings updated successfully",
      });

      return true;
    } catch (err) {
      console.error('❌ Settings update error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };

  const uploadFile = async (file: File, bucket: 'gym-logos' | 'gym-documents') => {
    try {
      console.log('📤 Starting file upload:', { bucket, fileName: file.name });
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file);

      console.log('📤 Upload result:', { error: uploadError });

      if (uploadError) throw uploadError;

      // Return the file key (path) instead of the public URL
      return fileName;
    } catch (err) {
      console.error('❌ File upload error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: "Upload Error",
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    }
  };

  const removeFile = async (fileKey: string, bucket: 'gym-logos' | 'gym-documents') => {
    try {
      console.log('🗑️ Starting file removal:', { fileKey, bucket });
      
      const { error, data } = await supabase.storage
        .from(bucket)
        .remove([fileKey]);

      console.log('🔥 Storage removal result:', { error, data });

      if (error) throw error;

      return true;
    } catch (err) {
      console.error('❌ File removal error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: "Remove Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };

  const getSignedUrl = async (fileKey: string, bucket: 'gym-logos' | 'gym-documents') => {
    try {
      console.log('🔗 Getting signed URL for:', { fileKey, bucket });
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(fileKey, 3600); // 1 hour expiry

      if (error) throw error;

      console.log('✅ Signed URL generated:', data?.signedUrl);
      return data?.signedUrl || null;
    } catch (err) {
      console.error('❌ Error getting signed URL:', err);
      return null;
    }
  };

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchSettings();
    }
  }, [currentOrganization?.id]);

  return {
    settings,
    isLoading,
    error,
    updateSettings,
    uploadFile,
    removeFile,
    getSignedUrl,
    refetch: fetchSettings,
  };
};